import { useState, useEffect } from "react";
import { Building2, Plus, Search, ChevronRight, Edit2, Trash2, Bot, RefreshCw, ArrowLeft, MapPin, Phone, Users, Star, Briefcase } from "lucide-react";
import PrescripteurModal from "./components/grands-comptes/PrescripteurModal";
import RechercheIAModal from "./components/grands-comptes/RechercheIAModal";
import getApiUrl from './utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Badge de statut
const StatusBadge = ({ status }) => {
  const cfg = {
    prospect: "bg-slate-500/20 text-slate-400",
    contacte: "bg-blue-500/20 text-blue-400",
    actif: "bg-green-500/20 text-green-400",
    inactif: "bg-red-500/20 text-red-400"
  };
  const labels = { prospect: "Prospect", contacte: "Contacté", actif: "Actif", inactif: "Inactif" };
  return <span className={`px-2 py-1 text-xs rounded-full ${cfg[status] || cfg.prospect}`}>{labels[status] || status}</span>;
};

// Carte de stat
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className={`bg-${color}-900/30 border border-${color}-500/30 rounded-xl p-4`}>
    <Icon className={`w-6 h-6 text-${color}-400 mb-2`} />
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className={`text-sm text-${color}-300`}>{label}</p>
  </div>
);

const GrandsComptesDashboard = ({ onBack }) => {
  const [prescripteurs, setPrescripteurs] = useState([]);
  const [stats, setStats] = useState(null);
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showIA, setShowIA] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [typesRes, prescRes, statsRes] = await Promise.all([
        authFetch(`${API}/api/grands-comptes/types-prescripteurs`),
        authFetch(`${API}/api/grands-comptes/prescripteurs?search=${search}&type_prescripteur=${filterType}&statut=${filterStatut}`),
        authFetch(`${API}/api/grands-comptes/stats`)
      ]);
      if (typesRes.ok) setTypes((await typesRes.json()).types);
      if (prescRes.ok) setPrescripteurs((await prescRes.json()).prescripteurs);
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [search, filterType, filterStatut]);

  const deletePrescripteur = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await authFetch(`${API}/api/grands-comptes/prescripteurs/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400"><ArrowLeft className="w-6 h-6" /></button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3"><Briefcase className="w-8 h-8 text-amber-400" />Grands Comptes</h1>
              <p className="text-slate-400">CCAS, CARSAT, MDPH et prescripteurs</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowIA(true)} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"><Bot className="w-5 h-5" />Recherche IA</button>
            <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center gap-2"><Plus className="w-5 h-5" />Nouveau</button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <StatCard icon={Building2} label="Prescripteurs" value={stats.prescripteurs?.total || 0} color="blue" />
            <StatCard icon={Building2} label="Actifs" value={stats.prescripteurs?.par_statut?.actif || 0} color="green" />
            <StatCard icon={Building2} label="Prospects" value={stats.prescripteurs?.par_statut?.prospect || 0} color="yellow" />
            <StatCard icon={Building2} label="Dossiers" value={stats.dossiers?.total || 0} color="purple" />
            <StatCard icon={Building2} label="Conversion" value={`${stats.dossiers?.taux_conversion || 0}%`} color="cyan" />
            <StatCard icon={Building2} label="CA généré" value={`${(stats.ca_genere || 0).toLocaleString()}€`} color="emerald" />
          </div>
        )}

        {/* Filtres */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white">
            <option value="">Tous types</option>
            {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v.nom_complet}</option>)}
          </select>
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white">
            <option value="">Tous statuts</option>
            <option value="prospect">Prospects</option>
            <option value="contacte">Contactés</option>
            <option value="actif">Actifs</option>
            <option value="inactif">Inactifs</option>
          </select>
          <button onClick={load} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg"><RefreshCw className="w-5 h-5 text-slate-400" /></button>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-blue-400 animate-spin" /></div>
        ) : prescripteurs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {prescripteurs.map(p => (
              <div key={p.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4 hover:border-slate-600">
                <div className="flex justify-between mb-3">
                  <div>
                    <h3 className="text-white font-semibold">{p.nom_organisme}</h3>
                    <p className="text-sm text-slate-400">{types[p.type_prescripteur]?.nom_complet || p.type_prescripteur}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.score_potentiel && <span className="text-yellow-400 flex items-center gap-1 text-sm"><Star className="w-4 h-4" />{p.score_potentiel}</span>}
                    <StatusBadge status={p.statut} />
                  </div>
                </div>
                <div className="space-y-1 text-sm text-slate-400 mb-3">
                  {p.ville && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" />{p.code_postal} {p.ville}</p>}
                  {p.telephone_standard && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); window.ringoverDial && window.ringoverDial(p.telephone_standard); }}
                      className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 hover:underline"
                    >
                      <Phone className="w-4 h-4" />{p.telephone_standard}
                    </button>
                  )}
                  {p.contacts?.length > 0 && <p className="flex items-center gap-2"><Users className="w-4 h-4" />{p.contacts.length} contact(s)</p>}
                </div>
                <div className="flex justify-between pt-3 border-t border-slate-700">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-slate-700 rounded text-blue-400"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deletePrescripteur(p.id)} className="p-1.5 hover:bg-slate-700 rounded text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            <Building2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">Aucun prescripteur</p>
            <button onClick={() => setShowIA(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg inline-flex items-center gap-2"><Bot className="w-5 h-5" />Recherche IA</button>
          </div>
        )}
      </div>

      {(showCreate || editing) && <PrescripteurModal prescripteur={editing} typesPrescripteurs={types} onSave={load} onClose={() => { setShowCreate(false); setEditing(null); }} />}
      {showIA && <RechercheIAModal typesPrescripteurs={types} onImport={load} onClose={() => setShowIA(false)} />}
    </div>
  );
};

export default GrandsComptesDashboard;
